import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, useAuth } from '../App';
import { toast } from 'sonner';
import Sidebar from '../components/layout/Sidebar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  ArrowLeft,
  Mail,
  Phone,
  Building,
  MapPin,
  Plus,
  Send,
  CheckCircle,
  Clock,
  Edit,
  MessageSquare,
  FileText,
  Activity,
  DollarSign,
  ClipboardList,
  AlertTriangle,
  Circle
} from 'lucide-react';
import ClientLoansSection from '../components/ClientLoansSection';

const LOAN_TYPES = ['DSCR', 'HELOC', 'Personal', 'Construction', 'Other'];
const DEAL_STAGES = ['New Lead', 'Contacted', 'Application Started', 'Docs Received', 'Underwriting', 'Approved', 'Closed', 'Lost'];
const TASK_TYPES = ['call', 'email_follow_up', 'request_docs', 'meeting', 'other'];
const COMMISSION_STATUSES = ['Pending', 'Invoiced', 'Paid', 'Cancelled'];

const ContactDetailPage = () => {
  const { contactId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [contact, setContact] = useState(null);
  const [deals, setDeals] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [activities, setActivities] = useState([]);
  const [sentEmails, setSentEmails] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Deal checklists
  const [dealChecklists, setDealChecklists] = useState({});
  const [selectedDealForChecklist, setSelectedDealForChecklist] = useState(null);
  
  // Edit mode
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  
  // Dialogs
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [showDealDialog, setShowDealDialog] = useState(false);
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [showChecklistDialog, setShowChecklistDialog] = useState(false);
  
  // Email form
  const [emailForm, setEmailForm] = useState({
    template_id: '',
    subject: '',
    body_html: ''
  });
  
  // Deal form
  const [dealForm, setDealForm] = useState({
    loan_type: '',
    amount: '',
    property_type: '',
    property_address: '',
    stage: 'New Lead',
    commission_amount: '',
    commission_status: ''
  });
  
  // Task form
  const [taskForm, setTaskForm] = useState({
    type: 'call',
    title: '',
    notes: '',
    due_at: new Date().toISOString().split('T')[0]
  });
  
  // Note
  const [noteText, setNoteText] = useState('');

  useEffect(() => {
    loadContactData();
  }, [contactId]);

  const loadContactData = async () => {
    try {
      const [contactRes, dealsRes, tasksRes, activitiesRes, emailsRes, templatesRes] = await Promise.all([
        api.get(`/contacts/${contactId}`),
        api.get(`/deals?contact_id=${contactId}`),
        api.get(`/tasks?contact_id=${contactId}`),
        api.get(`/activities?contact_id=${contactId}`),
        api.get(`/emails/sent?contact_id=${contactId}`),
        api.get('/templates')
      ]);
      
      setContact(contactRes.data);
      setEditData(contactRes.data);
      setDeals(dealsRes.data);
      setTasks(tasksRes.data);
      setActivities(activitiesRes.data);
      setSentEmails(emailsRes.data);
      setTemplates(templatesRes.data);
      
      // Load checklists for all deals
      const checklistPromises = dealsRes.data.map(deal => 
        api.get(`/deals/${deal.deal_id}/checklists`).catch(() => ({ data: [] }))
      );
      const checklistResults = await Promise.all(checklistPromises);
      
      const checklistMap = {};
      dealsRes.data.forEach((deal, idx) => {
        checklistMap[deal.deal_id] = checklistResults[idx].data;
      });
      setDealChecklists(checklistMap);
      
    } catch (error) {
      toast.error('Failed to load contact data');
      navigate('/contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveContact = async () => {
    try {
      await api.put(`/contacts/${contactId}`, editData);
      setContact(editData);
      setIsEditing(false);
      toast.success('Contact updated successfully');
    } catch (error) {
      toast.error('Failed to update contact');
    }
  };

  const handleSendEmail = async () => {
    try {
      await api.post('/emails/send', {
        contact_id: contactId,
        subject: emailForm.subject,
        body_html: emailForm.body_html
      });
      toast.success('Email sent successfully!');
      setShowEmailDialog(false);
      setEmailForm({ template_id: '', subject: '', body_html: '' });
      loadContactData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    }
  };

  const handleTemplateSelect = async (templateId) => {
    if (!templateId) return;
    
    try {
      const res = await api.post(`/templates/${templateId}/preview?contact_id=${contactId}`);
      setEmailForm({
        template_id: templateId,
        subject: res.data.subject,
        body_html: res.data.body_html
      });
    } catch (error) {
      toast.error('Failed to load template');
    }
  };

  const handleCreateDeal = async () => {
    try {
      await api.post('/deals', {
        contact_id: contactId,
        ...dealForm,
        amount: dealForm.amount ? parseFloat(dealForm.amount) : null,
        commission_amount: dealForm.commission_amount ? parseFloat(dealForm.commission_amount) : null
      });
      toast.success('Deal created successfully');
      setShowDealDialog(false);
      setDealForm({ loan_type: '', amount: '', property_type: '', property_address: '', stage: 'New Lead', commission_amount: '', commission_status: '' });
      loadContactData();
    } catch (error) {
      toast.error('Failed to create deal');
    }
  };

  const handleCreateTask = async () => {
    try {
      await api.post('/tasks', {
        contact_id: contactId,
        type: taskForm.type,
        title: taskForm.title,
        notes: taskForm.notes,
        due_at: new Date(taskForm.due_at).toISOString()
      });
      toast.success('Task created successfully');
      setShowTaskDialog(false);
      setTaskForm({ type: 'call', title: '', notes: '', due_at: new Date().toISOString().split('T')[0] });
      loadContactData();
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      await api.put(`/tasks/${taskId}`, {
        completed_at: new Date().toISOString()
      });
      toast.success('Task completed');
      loadContactData();
    } catch (error) {
      toast.error('Failed to complete task');
    }
  };

  const handleAddNote = async () => {
    try {
      await api.post(`/activities/note?contact_id=${contactId}&note=${encodeURIComponent(noteText)}`);
      toast.success('Note added');
      setShowNoteDialog(false);
      setNoteText('');
      loadContactData();
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const handleUpdateDealStage = async (dealId, newStage) => {
    try {
      // First validate
      const validationRes = await api.post(
        `/deals/${dealId}/validate-stage-move?target_stage=${encodeURIComponent(newStage)}`
      );
      
      if (!validationRes.data.can_move) {
        toast.error(validationRes.data.message, {
          description: `Incomplete: ${validationRes.data.incomplete_items.join(', ')}`
        });
        return;
      }
      
      await api.put(`/deals/${dealId}`, { stage: newStage });
      toast.success('Deal stage updated');
      loadContactData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'object' && detail.incomplete_items) {
        toast.error(detail.message, {
          description: `Incomplete: ${detail.incomplete_items.join(', ')}`
        });
      } else {
        toast.error('Failed to update deal');
      }
    }
  };

  const handleUpdateDealCommission = async (dealId, commissionAmount, commissionStatus) => {
    try {
      await api.put(`/deals/${dealId}`, { 
        commission_amount: parseFloat(commissionAmount) || null,
        commission_status: commissionStatus || null
      });
      toast.success('Commission info updated');
      loadContactData();
    } catch (error) {
      toast.error('Failed to update commission');
    }
  };

  const handleChecklistItemToggle = async (dealId, stage, itemId, completed) => {
    try {
      await api.put(`/deals/${dealId}/checklists/${encodeURIComponent(stage)}/items/${itemId}?completed=${completed}`);
      toast.success(completed ? 'Item completed' : 'Item unchecked');
      
      // Refresh checklists for this deal
      const checklistRes = await api.get(`/deals/${dealId}/checklists`);
      setDealChecklists(prev => ({
        ...prev,
        [dealId]: checklistRes.data
      }));
    } catch (error) {
      toast.error('Failed to update checklist item');
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'email_sent': return <Mail className="h-4 w-4" />;
      case 'reply_received': return <MessageSquare className="h-4 w-4" />;
      case 'note_added': return <FileText className="h-4 w-4" />;
      case 'stage_changed': return <Activity className="h-4 w-4" />;
      case 'task_created': return <Clock className="h-4 w-4" />;
      case 'task_completed': return <CheckCircle className="h-4 w-4" />;
      case 'deal_created': return <DollarSign className="h-4 w-4" />;
      case 'checklist_item_updated': return <ClipboardList className="h-4 w-4" />;
      default: return <Activity className="h-4 w-4" />;
    }
  };

  const getChecklistProgress = (dealId, stage) => {
    const checklists = dealChecklists[dealId] || [];
    const checklist = checklists.find(c => c.stage === stage);
    if (!checklist) return null;
    
    const items = checklist.items || [];
    const total = items.length;
    const completed = items.filter(i => i.completed).length;
    const requiredTotal = items.filter(i => i.required).length;
    const requiredCompleted = items.filter(i => i.required && i.completed).length;
    
    return { total, completed, requiredTotal, requiredCompleted };
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-background">
        <Sidebar />
        <main className="flex-1 ml-64 p-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-muted rounded w-48"></div>
            <div className="grid grid-cols-3 gap-6">
              <div className="col-span-2 h-96 bg-muted rounded-xl"></div>
              <div className="h-96 bg-muted rounded-xl"></div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!contact) return null;

  return (
    <div className="flex min-h-screen bg-background" data-testid="contact-detail-page">
      <Sidebar />
      <main className="flex-1 ml-64 p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/contacts')} data-testid="back-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <Avatar className="h-16 w-16">
              <AvatarFallback className="bg-primary/10 text-primary text-xl font-semibold">
                {contact.first_name?.[0]}{contact.last_name?.[0]}
              </AvatarFallback>
            </Avatar>
            <div>
              <h1 className="text-2xl font-heading font-bold">
                {contact.first_name} {contact.last_name}
              </h1>
              <div className="flex items-center gap-2 mt-1">
                {contact.company && (
                  <span className="text-muted-foreground flex items-center gap-1">
                    <Building className="h-4 w-4" />
                    {contact.company}
                  </span>
                )}
                {contact.do_not_contact && (
                  <Badge variant="destructive">Do Not Contact</Badge>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            {isEditing ? (
              <>
                <Button variant="outline" onClick={() => setIsEditing(false)}>Cancel</Button>
                <Button onClick={handleSaveContact} data-testid="save-contact-btn">Save Changes</Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setIsEditing(true)} data-testid="edit-contact-btn">
                  <Edit className="mr-2 h-4 w-4" />
                  Edit
                </Button>
                <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
                  <DialogTrigger asChild>
                    <Button data-testid="send-email-btn">
                      <Send className="mr-2 h-4 w-4" />
                      Send Email
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>Send Email to {contact.first_name}</DialogTitle>
                      <DialogDescription>
                        Compose and send an email to this contact.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Use Template</Label>
                        <Select onValueChange={handleTemplateSelect}>
                          <SelectTrigger data-testid="template-select">
                            <SelectValue placeholder="Select a template..." />
                          </SelectTrigger>
                          <SelectContent>
                            {templates.map(t => (
                              <SelectItem key={t.template_id} value={t.template_id}>
                                {t.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>To</Label>
                        <Input value={contact.email} disabled />
                      </div>
                      <div className="space-y-2">
                        <Label>Subject</Label>
                        <Input
                          value={emailForm.subject}
                          onChange={(e) => setEmailForm({...emailForm, subject: e.target.value})}
                          placeholder="Email subject..."
                          data-testid="email-subject"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Message</Label>
                        <Textarea
                          value={emailForm.body_html}
                          onChange={(e) => setEmailForm({...emailForm, body_html: e.target.value})}
                          placeholder="Write your message..."
                          rows={8}
                          data-testid="email-body"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowEmailDialog(false)}>Cancel</Button>
                      <Button onClick={handleSendEmail} disabled={!emailForm.subject || !emailForm.body_html} data-testid="send-email-submit">
                        Send Email
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Contact Info & Deals */}
          <div className="lg:col-span-2 space-y-6">
            {/* Contact Info Card */}
            <Card data-testid="contact-info-card">
              <CardHeader>
                <CardTitle className="font-heading">Contact Information</CardTitle>
              </CardHeader>
              <CardContent>
                {isEditing ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>First Name</Label>
                      <Input
                        value={editData.first_name}
                        onChange={(e) => setEditData({...editData, first_name: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Last Name</Label>
                      <Input
                        value={editData.last_name}
                        onChange={(e) => setEditData({...editData, last_name: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input
                        value={editData.email}
                        onChange={(e) => setEditData({...editData, email: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Phone</Label>
                      <Input
                        value={editData.phone || ''}
                        onChange={(e) => setEditData({...editData, phone: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Company</Label>
                      <Input
                        value={editData.company || ''}
                        onChange={(e) => setEditData({...editData, company: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Source</Label>
                      <Input
                        value={editData.source || ''}
                        onChange={(e) => setEditData({...editData, source: e.target.value})}
                      />
                    </div>
                    <div className="col-span-2 space-y-2">
                      <Label>Property Address</Label>
                      <Input
                        value={editData.property_address || ''}
                        onChange={(e) => setEditData({...editData, property_address: e.target.value})}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-6">
                    <div className="flex items-center gap-3">
                      <Mail className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Email</p>
                        <p className="font-medium">{contact.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Phone className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Phone</p>
                        <p className="font-medium">{contact.phone || '-'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Building className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Company</p>
                        <p className="font-medium">{contact.company || '-'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <MapPin className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Property Address</p>
                        <p className="font-medium">{contact.property_address || '-'}</p>
                      </div>
                    </div>
                    <div className="col-span-2">
                      <p className="text-sm text-muted-foreground mb-2">Loan Types Interested In</p>
                      <div className="flex flex-wrap gap-2">
                        {contact.loan_types?.map(type => (
                          <Badge key={type} variant="secondary">{type}</Badge>
                        ))}
                        {(!contact.loan_types || contact.loan_types.length === 0) && (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Deals Card with Checklist */}
            <Card data-testid="deals-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading">Deals & Underwriting</CardTitle>
                  <Dialog open={showDealDialog} onOpenChange={setShowDealDialog}>
                    <DialogTrigger asChild>
                      <Button size="sm" data-testid="add-deal-btn">
                        <Plus className="mr-2 h-4 w-4" />
                        Add Deal
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Create New Deal</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label>Loan Type *</Label>
                          <Select value={dealForm.loan_type} onValueChange={(v) => setDealForm({...dealForm, loan_type: v})}>
                            <SelectTrigger data-testid="deal-loan-type">
                              <SelectValue placeholder="Select loan type" />
                            </SelectTrigger>
                            <SelectContent>
                              {LOAN_TYPES.map(type => (
                                <SelectItem key={type} value={type}>{type}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Amount</Label>
                          <Input
                            type="number"
                            value={dealForm.amount}
                            onChange={(e) => setDealForm({...dealForm, amount: e.target.value})}
                            placeholder="e.g., 500000"
                            data-testid="deal-amount"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Property Type</Label>
                          <Input
                            value={dealForm.property_type}
                            onChange={(e) => setDealForm({...dealForm, property_type: e.target.value})}
                            placeholder="e.g., Single Family, Multi-Family"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Stage</Label>
                          <Select value={dealForm.stage} onValueChange={(v) => setDealForm({...dealForm, stage: v})}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {DEAL_STAGES.map(stage => (
                                <SelectItem key={stage} value={stage}>{stage}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setShowDealDialog(false)}>Cancel</Button>
                        <Button onClick={handleCreateDeal} disabled={!dealForm.loan_type} data-testid="save-deal-btn">
                          Create Deal
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {deals.length > 0 ? (
                  <Accordion type="single" collapsible className="space-y-3">
                    {deals.map(deal => {
                      const checklists = dealChecklists[deal.deal_id] || [];
                      const currentStageProgress = getChecklistProgress(deal.deal_id, deal.stage);
                      
                      return (
                        <AccordionItem
                          key={deal.deal_id}
                          value={deal.deal_id}
                          className="border rounded-lg px-4"
                          data-testid={`deal-${deal.deal_id}`}
                        >
                          <AccordionTrigger className="hover:no-underline">
                            <div className="flex items-center justify-between w-full pr-4">
                              <div className="flex items-center gap-3">
                                <Badge>{deal.loan_type}</Badge>
                                {deal.amount && (
                                  <span className="font-semibold text-primary">
                                    ${deal.amount.toLocaleString()}
                                  </span>
                                )}
                                <Badge variant="outline">{deal.stage}</Badge>
                              </div>
                              {currentStageProgress && (
                                <div className="flex items-center gap-2">
                                  <Progress 
                                    value={(currentStageProgress.completed / currentStageProgress.total) * 100} 
                                    className="w-20 h-2" 
                                  />
                                  <span className="text-xs text-muted-foreground">
                                    {currentStageProgress.completed}/{currentStageProgress.total}
                                  </span>
                                  {currentStageProgress.requiredCompleted < currentStageProgress.requiredTotal && (
                                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                                  )}
                                </div>
                              )}
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="pt-4">
                            <div className="space-y-4">
                              {/* Stage Selector */}
                              <div className="flex items-center gap-4">
                                <Label>Stage:</Label>
                                <Select value={deal.stage} onValueChange={(v) => handleUpdateDealStage(deal.deal_id, v)}>
                                  <SelectTrigger className="w-[200px]">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {DEAL_STAGES.map(stage => (
                                      <SelectItem key={stage} value={stage}>{stage}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              
                              {/* Commission Info (for Closed stage) */}
                              {(deal.stage === 'Approved' || deal.stage === 'Closed') && (
                                <div className="p-4 bg-muted/50 rounded-lg space-y-3">
                                  <h4 className="font-semibold flex items-center gap-2">
                                    <DollarSign className="h-4 w-4" />
                                    Commission Information
                                  </h4>
                                  <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                      <Label>Commission Amount</Label>
                                      <Input
                                        type="number"
                                        placeholder="Enter amount"
                                        defaultValue={deal.commission_amount || ''}
                                        onBlur={(e) => handleUpdateDealCommission(deal.deal_id, e.target.value, deal.commission_status)}
                                        data-testid={`commission-amount-${deal.deal_id}`}
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <Label>Commission Status</Label>
                                      <Select 
                                        value={deal.commission_status || ''} 
                                        onValueChange={(v) => handleUpdateDealCommission(deal.deal_id, deal.commission_amount, v)}
                                      >
                                        <SelectTrigger data-testid={`commission-status-${deal.deal_id}`}>
                                          <SelectValue placeholder="Select status" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {COMMISSION_STATUSES.map(status => (
                                            <SelectItem key={status} value={status}>{status}</SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  </div>
                                  {deal.commission_logged && (
                                    <Badge className="bg-green-500">Commission Logged</Badge>
                                  )}
                                </div>
                              )}
                              
                              {/* Checklists */}
                              {checklists.length > 0 && (
                                <div className="space-y-3">
                                  <h4 className="font-semibold flex items-center gap-2">
                                    <ClipboardList className="h-4 w-4" />
                                    Underwriting Checklist
                                  </h4>
                                  {checklists.map(checklist => {
                                    const progress = getChecklistProgress(deal.deal_id, checklist.stage);
                                    const isCurrentStage = checklist.stage === deal.stage;
                                    
                                    return (
                                      <div 
                                        key={checklist.checklist_id} 
                                        className={`p-3 rounded-lg border ${isCurrentStage ? 'border-primary bg-primary/5' : 'border-border'}`}
                                      >
                                        <div className="flex items-center justify-between mb-2">
                                          <span className="font-medium text-sm flex items-center gap-2">
                                            {checklist.stage}
                                            {isCurrentStage && <Badge variant="secondary" className="text-xs">Current</Badge>}
                                          </span>
                                          {progress && (
                                            <span className="text-xs text-muted-foreground">
                                              {progress.completed}/{progress.total} complete
                                            </span>
                                          )}
                                        </div>
                                        <div className="space-y-2">
                                          {checklist.items.map(item => (
                                            <div 
                                              key={item.item_id} 
                                              className="flex items-center gap-3"
                                              data-testid={`checklist-item-${item.item_id}`}
                                            >
                                              <Checkbox
                                                checked={item.completed}
                                                onCheckedChange={(checked) => 
                                                  handleChecklistItemToggle(deal.deal_id, checklist.stage, item.item_id, checked)
                                                }
                                                disabled={!isCurrentStage && DEAL_STAGES.indexOf(checklist.stage) > DEAL_STAGES.indexOf(deal.stage)}
                                              />
                                              <span className={`text-sm flex-1 ${item.completed ? 'line-through text-muted-foreground' : ''}`}>
                                                {item.label}
                                                {item.required && <span className="text-red-500 ml-1">*</span>}
                                              </span>
                                              {item.completed && item.completed_at && (
                                                <span className="text-xs text-muted-foreground">
                                                  {new Date(item.completed_at).toLocaleDateString()}
                                                </span>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      );
                    })}
                  </Accordion>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <DollarSign className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No deals yet</p>
                    <p className="text-sm">Create a deal to track this opportunity</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Client Loans & Lending Score */}
            <ClientLoansSection contactId={contactId} contact={contact} />
          </div>

          {/* Right Column - Tasks & Activity */}
          <div className="space-y-6">
            {/* Tasks Card */}
            <Card data-testid="tasks-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading">Tasks</CardTitle>
                  <Dialog open={showTaskDialog} onOpenChange={setShowTaskDialog}>
                    <DialogTrigger asChild>
                      <Button size="sm" variant="outline" data-testid="add-task-btn">
                        <Plus className="h-4 w-4" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Create Task</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label>Type</Label>
                          <Select value={taskForm.type} onValueChange={(v) => setTaskForm({...taskForm, type: v})}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {TASK_TYPES.map(type => (
                                <SelectItem key={type} value={type}>
                                  {type.replace('_', ' ')}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Title</Label>
                          <Input
                            value={taskForm.title}
                            onChange={(e) => setTaskForm({...taskForm, title: e.target.value})}
                            placeholder="e.g., Follow up call"
                            data-testid="task-title"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Due Date</Label>
                          <Input
                            type="date"
                            value={taskForm.due_at}
                            onChange={(e) => setTaskForm({...taskForm, due_at: e.target.value})}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Notes</Label>
                          <Textarea
                            value={taskForm.notes}
                            onChange={(e) => setTaskForm({...taskForm, notes: e.target.value})}
                            placeholder="Add any notes..."
                          />
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setShowTaskDialog(false)}>Cancel</Button>
                        <Button onClick={handleCreateTask} disabled={!taskForm.title} data-testid="save-task-btn">
                          Create Task
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[200px]">
                  {tasks.length > 0 ? (
                    <div className="space-y-2">
                      {tasks.map(task => (
                        <div
                          key={task.task_id}
                          className={`p-3 rounded-lg ${task.completed_at ? 'bg-muted/30' : 'bg-muted/50'}`}
                          data-testid={`task-${task.task_id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => !task.completed_at && handleCompleteTask(task.task_id)}
                                className="text-muted-foreground hover:text-primary"
                              >
                                {task.completed_at ? (
                                  <CheckCircle className="h-5 w-5 text-green-500" />
                                ) : (
                                  <Circle className="h-5 w-5" />
                                )}
                              </button>
                              <span className={task.completed_at ? 'line-through text-muted-foreground' : ''}>
                                {task.title}
                              </span>
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1 ml-7">
                            {task.type.replace('_', ' ')} • Due {new Date(task.due_at).toLocaleDateString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-muted-foreground">
                      <p className="text-sm">No tasks</p>
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Activity Timeline */}
            <Card data-testid="activity-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading">Activity</CardTitle>
                  <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
                    <DialogTrigger asChild>
                      <Button size="sm" variant="outline" data-testid="add-note-btn">
                        <MessageSquare className="h-4 w-4" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Add Note</DialogTitle>
                      </DialogHeader>
                      <div className="py-4">
                        <Textarea
                          value={noteText}
                          onChange={(e) => setNoteText(e.target.value)}
                          placeholder="Add a note about this contact..."
                          rows={4}
                          data-testid="note-textarea"
                        />
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setShowNoteDialog(false)}>Cancel</Button>
                        <Button onClick={handleAddNote} disabled={!noteText.trim()} data-testid="save-note-btn">
                          Add Note
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  {activities.length > 0 ? (
                    <div className="space-y-4">
                      {activities.map(activity => (
                        <div key={activity.activity_id} className="flex gap-3">
                          <div className="p-2 bg-muted rounded-lg h-fit">
                            {getActivityIcon(activity.type)}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-sm">{activity.title}</p>
                            {activity.details && (
                              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                                {activity.details}
                              </p>
                            )}
                            <p className="text-xs text-muted-foreground mt-1">
                              {new Date(activity.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Activity className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No activity yet</p>
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ContactDetailPage;
